/**
 * Shared content utility functions for profile views.
 *
 * These pure functions are used by CommunityProfilePage,
 * ProfilePlaylistsSection, and related profile components
 * to transform GraphQL playlist/content data into UI-ready shapes.
 */

import _get from 'lodash/get'
import { fromJS, Map as ImmutableMap } from 'immutable'
import { getLearnerLevelLabelFromFeatureMedia } from '../../../content/index'
import type {
  ContentReference,
  FlattenedReference,
  ImageContentRelationship,
  Playlist,
  PlaylistContentItem,
  PlaylistItem,
  PlaylistItemShell,
  PlaylistShell,
  TileDataShape,
  EnrichedPlaylistItem,
} from '../../types'
import type {
  OracleContentEntity,
  OraclePlaylistItem,
} from '../../oracle/useOracleSearch'
import { getDominantRainbowCategory } from './utils'

export const SYSTEM_PLAYLIST_TYPES = ['default', 'favorites']

/**
 * Wraps a JS tile-data shape into an Immutable Map. `fromJS` returns the
 * wide JS-to-Immutable union, so this is the documented Immutable boundary
 * cast for tile data — every tile-enrichment site funnels through here.
 */
export function toImmutableTile (
  tileData: TileDataShape,
): ImmutableMap<string, unknown> {
  return fromJS(tileData) as ImmutableMap<string, unknown>
}

/**
 * Type guard distinguishing Oracle-search content (which carries
 * series-level `seasonsCount` / `episodesCount`) from the plain
 * `PlaylistContentItem` shape.
 */
function isOracleContentEntity (
  content: PlaylistContentItem | OracleContentEntity,
): content is OracleContentEntity {
  return 'seasonsCount' in content || 'episodesCount' in content
}

/**
 * Excludes system playlists and the synthetic "default" named playlist from
 * user-facing lists (my playlists, member profile playlists).
 */
export function filterUserFacingPlaylists (playlists: Playlist[]): Playlist[] {
  const source = playlists || []
  return source.filter(
    (pl) => pl && pl.id
      && !SYSTEM_PLAYLIST_TYPES.includes(pl.playlistType || '')
      && (pl.playlistName || '').toLowerCase() !== 'default',
  )
}

/**
 * Derives a display year from a createdAt timestamp.
 * Returns the four-digit year as a number, or undefined when the input is falsy.
 */
export function getMemberSinceYear (
  createdAt: string | null | undefined,
): number | undefined {
  if (!createdAt) return undefined
  return new Date(createdAt).getFullYear()
}

export function flattenReferences (refs: ContentReference[]): FlattenedReference[] {
  if (!refs || !refs.length) return []
  return refs.reduce((acc: FlattenedReference[], ref: ContentReference) => {
    const type = _get(ref, 'contentReferenceType.type', '')
    const targets = ref.contentTo || []
    targets.forEach((to) => {
      const cId = to.contentId != null ? String(to.contentId) : ''
      acc.push({
        type,
        reference_type: type,
        name: to.title || '',
        title: to.title || '',
        path: to.path || '',
        contentId: cId,
        content_id: cId,
        language: to.language || '',
      })
    })
    return acc
  }, [])
}

/**
 * Converts a GraphQL content object into the shape TileV2Graphql expects.
 * TileV2Graphql resolves thumbnails from an `images` list of { type, url }
 * objects via getGraphqlHeroImage (default lookup: d6-keyart-with-text).
 */
export function buildTileData (
  content: PlaylistContentItem | OracleContentEntity | null,
): TileDataShape | null {
  if (!content) return null

  const assetUrl = _get(content, 'contentAssetReferences[0].asset.renderUrl', '')
  const legacyUrl = _get(content, 'imageContentRelationships[0].image.url', '')

  const imageRelationships: ImageContentRelationship[] = content.imageContentRelationships || []
  const images = imageRelationships.map((rel: ImageContentRelationship) => ({
    type: rel.type || '',
    url: _get(rel, 'image.url', ''),
  }))
  if (assetUrl && !images.some((img) => img.type === 'd6-keyart-no-text')) {
    images.push({ type: 'd6-keyart-no-text', url: assetUrl })
  }
  if (!images.some((img) => img.type === 'd6-keyart-with-text') && (assetUrl || legacyUrl)) {
    images.push({ type: 'd6-keyart-with-text', url: assetUrl || legacyUrl })
  }

  const series = _get(content, 'contentSeries[0]', null)

  // Vote component expects voteId as a number — coerce contentId
  const rawId = content.contentId || content.id
  const numericId = typeof rawId === 'string' ? Number(rawId) || rawId : rawId

  // Null-coerce so a `null` from GraphQL doesn't propagate to NaN inside the
  // hover's percentage math (`_get` only fills the default when undefined).
  const upVotes = _get(content, 'contentAnalytic.votesUpCountTotal', 0) ?? 0
  const totalVotes = _get(content, 'contentAnalytic.votesCountTotal', 0) ?? 0
  const commentsCount = _get(content, 'contentAnalytic.commentsCountTotal', 0) ?? 0

  return {
    id: numericId,
    nid: numericId,
    contentId: numericId,
    contentType: content.contentType || 'video',
    title: content.title || '',
    path: content.path || '',
    url: content.path || '',
    shortDescription: content.shortDescription || '',
    episodeNumber: content.episodeNumber,
    seasonNumber: content.seasonNumber,
    episode: content.episodeNumber,
    season: content.seasonNumber,
    instructor: content.instructor,
    cast: content.cast,
    primaryLanguage: content.primaryLanguage,
    // Drives the difficulty pill in Tile.v2GraphqlInfoMetadata's middle row;
    // matches the value getFormattedContentData() sets on Home tiles.
    learnerLevelLabel: getLearnerLevelLabelFromFeatureMedia(content.featureMedia),
    isNew: content.isNew,
    displayType: content.displayType,
    subType: content.subType,
    contentSubtype: content.subType,
    studio: content.studio,
    duration: _get(content, 'featureMedia.duration', 0),
    images,
    featureMedia: content.featureMedia || null,
    feature: {
      duration: _get(content, 'featureMedia.duration', 0),
      id: _get(content, 'featureMedia.mediaId', null),
    },
    references: flattenReferences(content.references || []),
    seriesTitle: series ? series.title : undefined,
    seriesPath: series ? series.path : undefined,
    seriesId: series ? series.contentId : undefined,
    hero: assetUrl || legacyUrl || '',
    heroAlt: content.title || '',
    image: assetUrl || legacyUrl || '',
    poster: legacyUrl || assetUrl || '',
    // Series-level totals for "5 seasons" display — only Oracle entities carry these
    seasonCount: isOracleContentEntity(content) ? content.seasonsCount || undefined : undefined,
    episodeCount: isOracleContentEntity(content) ? content.episodesCount || undefined : undefined,
    // TileV2Graphql reads data.getIn(['vote', 'upVote']) and data.getIn(['vote', 'downVote'])
    // so vote must be an object matching the shape produced by formatVote() in services/content.
    vote: {
      upVote: upVotes,
      downVote: Math.max(totalVotes - upVotes, 0),
    },
    commentsCount,
  }
}

/**
 * For an episode that belongs to a series, builds a SERIES-shaped tile so the
 * hover renders series-flavor metadata (Gaia|Original badge, content-type tag,
 * Yoga/Fitness/Meditation tags) with `featuredContent` set to the episode.
 * Mirrors the shape Home's `getFormattedContentData()` produces for Continue
 * Watching tiles. Returns null when the episode lacks a usable parent series —
 * caller should fall back to `buildTileData(episode)`.
 *
 * The series fragment must have been fetched with the same field set as
 * `PLAYLIST_ITEM_CONTENT_FIELDS.content` (studio, contentType, references,
 * featureMedia.mediaAssets, etc.) — see fragments.ts.
 */
export function buildEpisodeOfSeriesTile (
  episode: PlaylistContentItem | OracleContentEntity,
  options?: {
    userPosition?: number
    mediaDuration?: number
    mediaId?: string | null
  },
): TileDataShape | null {
  const series = _get(episode, 'contentSeries[0]', null) as PlaylistContentItem | null
  if (!series || !series.contentId) return null
  // Series entry without studio/contentType means the query didn't pull the
  // expanded fields — bail so the tile doesn't render with stub data.
  if (!series.contentType) return null

  const seriesTile = buildTileData(series)
  if (!seriesTile) return null

  const epUpVotes = _get(episode, 'contentAnalytic.votesUpCountTotal', 0) ?? 0
  const epTotalVotes = _get(episode, 'contentAnalytic.votesCountTotal', 0) ?? 0
  const epCommentsCount = _get(episode, 'contentAnalytic.commentsCountTotal', 0) ?? 0
  const epDuration = options?.mediaDuration ?? _get(episode, 'featureMedia.duration', 0) ?? 0
  const epPosition = options?.userPosition
    ?? _get(episode, 'featureMedia.userMediaPosition.position', 0)
    ?? 0
  const epMediaId = options?.mediaId ?? _get(episode, 'featureMedia.mediaId', null)
  const rawEpId = episode.contentId || episode.id
  const epNumericId = typeof rawEpId === 'string' ? Number(rawEpId) || rawEpId : rawEpId

  // Tile.v2GraphqlInfoMetadata reads featuredContent via data.get('featuredContent')
  // and uses featureMedia.userMediaPosition.position > 0 to flag in-progress.
  seriesTile.featuredContent = {
    contentId: epNumericId,
    title: episode.title || '',
    path: episode.path || '',
    contentType: episode.contentType || 'video',
    episodeNumber: episode.episodeNumber,
    seasonNumber: episode.seasonNumber,
    featureMedia: {
      duration: epDuration,
      mediaId: epMediaId,
      userMediaPosition: { position: epPosition },
    },
    vote: {
      upVote: epUpVotes,
      downVote: Math.max(epTotalVotes - epUpVotes, 0),
    },
    commentsCount: epCommentsCount,
  }

  return seriesTile
}

export interface EnrichPlaylistItemsOptions {
  // When true, drops items whose visible tile identity (tileData.contentId)
  // already appeared earlier in the list. Multiple episodes of the same
  // parent series pivot to the same series-shaped tile via
  // buildEpisodeOfSeriesTile, so without this they render as duplicates.
  dedupe?: boolean
  /**
   * When true (default, back-compat), episode rows whose content carries a
   * populated `contentSeries` are pivoted to a SERIES-shaped tile via
   * `buildEpisodeOfSeriesTile`. Required for SYSTEM playlists (Watch Later,
   * Favorites, etc.) where the surface mimics "Continue Watching".
   *
   * When false, every row renders as its own episode tile
   * (`tileData.contentType === 'video'`). Required for CUSTOM user-authored
   * playlists where the rendered list must match the playlist's stored
   * contents — the author chose specific episodes, so the UI must surface
   * those episodes (not the parent series).
   *
   * Pairs with `dedupe`: setting `dedupe: true` while `pivotEpisodesToSeries`
   * is false has no effect on series collisions (each episode has a distinct
   * contentId), but plain duplicate rows (same content added twice) still
   * collapse.
   */
  pivotEpisodesToSeries?: boolean
}

export function enrichPlaylistItems (
  items: (PlaylistItem | OraclePlaylistItem)[],
  options?: EnrichPlaylistItemsOptions,
): EnrichedPlaylistItem[] {
  const source = items || []
  const result: EnrichedPlaylistItem[] = []
  const seenTileIds = new Set<string>()
  for (let i = 0; i < source.length; i += 1) {
    const item = source[i]
    if (item && item.content && item.content.length > 0) {
      const c = item.content[0]
      const assetUrl = _get(c, 'contentAssetReferences[0].asset.renderUrl', '')
      const legacyUrl = _get(c, 'imageContentRelationships[0].image.url', '')
      // SYSTEM playlists: pivot episodes-of-series to a series-shaped tile so
      // hover labels match Home's Continue Watching pattern (caller passes
      // `pivotEpisodesToSeries: true`, the default for back-compat).
      // CUSTOM playlists: skip the pivot — render every row as its own
      // episode tile (`buildTileData(c)` keeps `contentType: 'video'` and
      // the episode title/thumbnail), preserving author intent.
      const pivotToSeries = options?.pivotEpisodesToSeries !== false
      const tileData = pivotToSeries
        ? (buildEpisodeOfSeriesTile(c) ?? buildTileData(c))
        : buildTileData(c)
      const tileKey = tileData ? String(tileData.contentId ?? '') : ''
      const isDuplicate = !!(options?.dedupe && tileKey && seenTileIds.has(tileKey))
      if (!isDuplicate) {
        if (tileKey) seenTileIds.add(tileKey)
        // Preserve the playlist-item ROW's own contentId / contentType for
        // delete/update mutations. The backend looks up the row by these
        // exact values, and they frequently differ from the tile's display
        // values (series-pivoted tiles use `'series'`; gaia originals report
        // `'gaiaOriginal'` on the resolved content). See plan: fix
        // `playlistDeletePlaylistItem` failing for series, gaia originals,
        // and regular videos.
        const rawRowContentId = item.contentId
        const rowContentIdNumber = rawRowContentId == null || rawRowContentId === ''
          ? NaN
          : Number(rawRowContentId)
        result.push({
          id: item.id,
          contentId: String(c.contentId ?? c.id ?? ''),
          playlistItemContentId: Number.isFinite(rowContentIdNumber) ? rowContentIdNumber : null,
          playlistItemContentType: item.contentType ?? null,
          // Carry the row's stored playlistPosition through so the reorder
          // controls can pass the destination's position to the mutation.
          // PlaylistItem.playlistPosition is already requested by
          // PLAYLIST_ITEM_CONTENT_FIELDS.
          playlistPosition: typeof item.playlistPosition === 'number'
            ? item.playlistPosition
            : null,
          title: c.title || '',
          path: c.path || '',
          imageUrl: assetUrl || legacyUrl || '',
          isAssetUrl: !!assetUrl,
          duration: _get(c, 'featureMedia.duration', 0),
          tileData,
          // Pre-convert to Immutable once during data fetch rather than on every
          // render — eliminates ~300ms of synchronous fromJS() on tab switches.
          tileDataImmutable: tileData ? toImmutableTile(tileData) : null,
        })
      }
    }
  }
  // Sort by `playlistPosition` DESC (highest = top of list) so the UI
  // reflects the order persisted by `playlistUpdatePlaylistItem`.  The
  // server's `Playlist.playlistItems` relation field has no `order` arg
  // and returns items in default DB order, so we sort client-side here.
  // Items with `playlistPosition: null` (e.g. watch-history rows) keep
  // their relative order via stable sort (ES2019+).
  result.sort((a, b) => {
    if (a.playlistPosition == null && b.playlistPosition == null) return 0
    if (a.playlistPosition == null) return 1
    if (b.playlistPosition == null) return -1
    return b.playlistPosition - a.playlistPosition
  })
  return result
}

/**
 * Extract the first language variant from a playlist item's content array.
 */
export function getFirstContent (
  item: { content?: PlaylistContentItem[] } | undefined | null,
): PlaylistContentItem | null {
  if (!item?.content || item.content.length === 0) return null
  return item.content[0]
}

/**
 * Extract the best available thumbnail URL from content metadata.
 * Checks contentAssetReferences first, then imageContentRelationships.
 */
export function getEpisodeThumbnail (content: PlaylistContentItem | null): string {
  if (!content) return ''
  const assets = content.contentAssetReferences || []
  if (assets[0]?.asset?.renderUrl) {
    return assets[0].asset.renderUrl
  }
  const relationships: ImageContentRelationship[] = content.imageContentRelationships || []
  for (let i = 0; i < relationships.length; i += 1) {
    if (relationships[i]?.image?.url) {
      return relationships[i].image.url
    }
  }
  return ''
}

/**
 * Derive a short category label from content metadata for the episode tag pill.
 * Falls back to subType or displayType when the main type is generic.
 */
export function getContentCategoryTag (
  content: PlaylistContentItem | null,
  watchLabel = 'Watch',
): string {
  if (!content) return ''
  if (content.subType) return content.subType
  if (content.displayType) return content.displayType
  const ct = content.contentType || ''
  if (ct === 'video' || ct === 'episode') return watchLabel
  return ct ? ct.charAt(0).toUpperCase() + ct.slice(1) : ''
}

export function computeTotalDuration (
  items: (PlaylistItem | OraclePlaylistItem)[],
): string | null {
  if (!items || items.length === 0) return null
  let totalSeconds = 0
  items.forEach((item) => {
    if (item && item.content && item.content.length > 0) {
      totalSeconds += _get(item.content[0], 'featureMedia.duration', 0)
    }
  })
  if (totalSeconds <= 0) return null
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  if (hours > 0) return `${hours}h ${minutes}m`
  return `${minutes}m`
}

/**
 * Formats a duration in seconds as mm:ss for individual episode display.
 * Distinct from the playlist-level `formatDuration` in theme/web-app which
 * produces human-readable strings like "1 hr 5 mins".
 */
export function formatEpisodeDuration (seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs < 10 ? '0' : ''}${secs}`
}

export interface PlaylistDetailsEntry {
  items: EnrichedPlaylistItem[]
  primaryTopic?: string
  totalDuration?: string | null
  /**
   * Shell-reported item count (`playlist.itemCount`).  Carried alongside the
   * (possibly-empty) hydrated `items` so row components can distinguish
   *   "playlist has N items, hydration still inflight" → render skeletons
   * from
   *   "playlist is genuinely empty" → render the empty-state callout.
   * Optional so legacy callers (`buildEnrichedPlaylistDetailsMap`) that don't
   * see itemCount don't have to populate it.
   */
  expectedItemCount?: number
}

/**
 * Builds an ID-keyed map of enriched playlist details (items, dominant topic,
 * total duration). Used by both useProfilePlaylists and useFollowedPlaylists.
 *
 * Uniform behavior across playlist types: every row (whether SYSTEM —
 * Watch Later / Favorites — or CUSTOM user-authored) renders every stored
 * item as its own episode tile, with no series-pivot and no dedupe.
 * Watch Later and Favorites previously mimicked the "Continue Watching"
 * collapse-to-series UX; that was walked back so the rendered tile count
 * always matches the playlist's stored `itemCount`.
 */
export function buildEnrichedPlaylistDetailsMap (
  playlists: Array<{
    id?: string
    playlistType?: string
    playlistItems?: (PlaylistItem | OraclePlaylistItem)[]
  }>,
): Record<string, PlaylistDetailsEntry> {
  const details: Record<string, PlaylistDetailsEntry> = {}
  playlists.forEach((playlist) => {
    if (!playlist || !playlist.id) return
    const items = playlist.playlistItems || []
    details[playlist.id] = {
      items: enrichPlaylistItems(items, {
        dedupe: false,
        pivotEpisodesToSeries: false,
      }),
      primaryTopic: getDominantRainbowCategory(items),
      totalDuration: computeTotalDuration(items),
    }
  })
  return details
}

/**
 * Lookup key for a shell item in the hydration `contentMap`. Prefers
 * `titleGroupId` (the composite `{contentId}-{contentType}` string the API
 * filters by); falls back to deriving it for shells that didn't select
 * `titleGroupId`. Mirrors `deriveTitleGroupId` in `useContentHydration.ts`
 * so caller / map producer stay in lockstep.
 */
function shellHydrationKey (it: PlaylistItemShell | null | undefined): string {
  if (!it) return ''
  if (it.titleGroupId) return String(it.titleGroupId)
  if (it.contentId && it.contentType) {
    return `${String(it.contentId)}-${String(it.contentType)}`
  }
  return ''
}

/**
 * Shell-shape sibling of `enrichPlaylistItems`. The shell query
 * (GET_MY_PLAYLISTS_SHELL / GET_FOLLOWED_PLAYLISTS_SHELL) returns
 * `PlaylistItemShell[]` carrying only identity + position; Content fields
 * are hydrated separately via `useContentHydration` (cache-and-network
 * GET_CONTENT). This adapter pairs each shell item with its hydrated
 * content from the map and runs the existing `buildTileData` /
 * `buildEpisodeOfSeriesTile` path so the produced `EnrichedPlaylistItem`s
 * are byte-compatible with the legacy flow.
 *
 * Items whose `titleGroupId` isn't yet in `contentMap` are skipped — they
 * appear once hydration resolves and the parent re-renders. Genuinely
 * deleted / unpublished items also fall here and currently drop silently
 * (see review S1 in the plan for the future "Content unavailable" UX).
 */
export function enrichShellPlaylistItems (
  shellItems: PlaylistItemShell[] | null | undefined,
  contentMap: ReadonlyMap<string, PlaylistContentItem>,
  options?: EnrichPlaylistItemsOptions,
): EnrichedPlaylistItem[] {
  const source = shellItems || []
  if (source.length === 0) return []

  // Wrap each shell item in a PlaylistItem-shape so enrichPlaylistItems can
  // do the heavy lifting unchanged. The wrapper sets `content: [hydrated]`
  // when the contentMap has the entry; rows with no hydrated content are
  // filtered out (enrichPlaylistItems skips items without content already,
  // but we filter ahead of time to keep the source array tight).
  const wrapped: PlaylistItem[] = source.reduce<PlaylistItem[]>((acc, it) => {
    const key = shellHydrationKey(it)
    if (!key) return acc
    const content = contentMap.get(key)
    if (!content) return acc
    acc.push({
      id: it.id,
      contentId: it.contentId,
      contentType: it.contentType,
      playlistPosition: it.playlistPosition,
      content: [content],
    })
    return acc
  }, [])
  return enrichPlaylistItems(wrapped, options)
}

/**
 * Per-playlist version of `buildShellPlaylistDetailsMap` — the entire
 * (enrich + topic + duration + expectedItemCount) work for ONE playlist.
 * Extracted so `createShellPlaylistDetailsBuilder` can cache results per
 * playlist id while sharing the exact same body the legacy wrapper uses
 * — no semantic drift between the cached and uncached paths.
 */
export function buildSingleShellPlaylistDetails (
  playlist: PlaylistShell,
  contentMap: ReadonlyMap<string, PlaylistContentItem>,
): PlaylistDetailsEntry {
  const shellItems = playlist.playlistItems || []

  // Uniform behavior across playlist types: render every shell item as its
  // own episode tile (no series-pivot, no dedupe). Watch Later and Favorites
  // previously collapsed series episodes to mimic Continue Watching; that
  // was walked back so the rendered tile count always matches the shell's
  // `itemCount`.
  const enriched = enrichShellPlaylistItems(shellItems, contentMap, {
    dedupe: false,
    pivotEpisodesToSeries: false,
  })

  // Build a synthetic PlaylistItem[] for getDominantRainbowCategory /
  // computeTotalDuration (they only need each item's content[0]). Skip
  // items whose content hasn't hydrated yet.
  const wrappedForUtils: PlaylistItem[] = shellItems.reduce<PlaylistItem[]>((acc, it) => {
    const key = shellHydrationKey(it)
    if (!key) return acc
    const content = contentMap.get(key)
    if (!content) return acc
    acc.push({
      id: it.id,
      contentId: it.contentId,
      contentType: it.contentType,
      playlistPosition: it.playlistPosition,
      content: [content],
    })
    return acc
  }, [])

  return {
    items: enriched,
    primaryTopic: getDominantRainbowCategory(wrappedForUtils),
    totalDuration: computeTotalDuration(wrappedForUtils),
    // Source of truth for "is this playlist actually empty vs still
    // hydrating?".  The shell reports the count even before tile content
    // arrives; row components use it to choose between skeleton tiles
    // and the empty callout (PR 11).
    expectedItemCount: playlist.itemCount ?? 0,
  }
}

/**
 * Flatten shell playlists into the deduped tuples the hydration hook
 * consumes. Dedupes by `titleGroupId` (the composite key the API filters
 * by); falls back to `${contentId}-${contentType}` for shells that didn't
 * select `titleGroupId`. Returns `{ contentId, contentType, titleGroupId }`
 * so `useContentHydration` can pass `titleGroupIds` to `GET_CONTENT`
 * directly.
 */
export function collectShellContentItems (
  playlists: Array<PlaylistShell | null | undefined>,
): Array<{ contentId: string; contentType: string; titleGroupId: string }> {
  const seen = new Set<string>()
  const result: Array<{ contentId: string; contentType: string; titleGroupId: string }> = []
  playlists.forEach((pl) => {
    if (!pl || !pl.playlistItems) return
    pl.playlistItems.forEach((it) => {
      const cid = it?.contentId ? String(it.contentId) : ''
      const ctype = it?.contentType || 'video'
      let tgid = it?.titleGroupId ? String(it.titleGroupId) : ''
      if (!tgid && cid) tgid = `${cid}-${ctype}`
      if (!tgid || seen.has(tgid)) return
      seen.add(tgid)
      result.push({ contentId: cid, contentType: ctype, titleGroupId: tgid })
    })
  })
  return result
}
